import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, getCategoryName, articles } from '@/mocks/articles';
import { generateSEOMeta, generateSchemaOrg } from '@/components/base/SEOData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3)
    : [];

  useEffect(() => {
    if (!article || !slug) return;

    const seoMeta = generateSEOMeta({
      title: article.title,
      description: article.excerpt,
      keywords: article.tags.join(',') + ',膀胱健康,泌尿健康,健康科普',
      canonicalUrl: `/article/${slug}`,
      ogType: 'article',
      ogImage: article.imageUrl,
      articleData: {
        publishedTime: article.publishDate,
        modifiedTime: article.publishDate,
        author: article.author,
        tags: article.tags,
      },
    });

    const existingMeta = document.querySelector('script[data-seo-meta]');
    if (existingMeta) existingMeta.remove();
    const metaScript = document.createElement('script');
    metaScript.setAttribute('data-seo-meta', 'true');
    metaScript.type = 'application/json';
    metaScript.textContent = seoMeta;
    document.head.appendChild(metaScript);

    const schema = generateSchemaOrg({
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.imageUrl,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      datePublished: article.publishDate,
      dateModified: article.publishDate,
      publisher: {
        '@type': 'Organization',
        name: '膀胱健康知识网',
      },
    });

    const existingSchema = document.querySelector('script[data-schema-org]');
    if (existingSchema) existingSchema.remove();
    const schemaScript = document.createElement('script');
    schemaScript.setAttribute('data-schema-org', 'true');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = schema;
    document.head.appendChild(schemaScript);

    document.title = `${article.title} - 膀胱健康知识网`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', article.excerpt);
  }, [article, slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <article>
          <div className="w-full bg-background-50">
            <div className="w-full max-w-[900px] mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: getCategoryName(article.category), path: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="w-full bg-background-50">
            <div className="w-full max-w-[900px] mx-auto px-4 md:px-6 pb-6">
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-full whitespace-nowrap">
                  {getCategoryName(article.category)}
                </span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 md:gap-4 text-sm text-foreground-500 mb-6">
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line text-xs"></i>
                  </div>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line text-xs"></i>
                  </div>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-xs"></i>
                  </div>
                  {article.readTime}阅读
                </span>
              </div>
            </div>
          </div>

          <div className="w-full bg-background-50">
            <div className="w-full max-w-[900px] mx-auto px-4 md:px-6 pb-8">
              <div className="w-full h-[300px] md:h-[400px] rounded-lg overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          <div className="w-full bg-background-50">
            <div className="w-full max-w-[800px] mx-auto px-4 md:px-6 pb-12 md:pb-16">
              <div
                className="prose prose-sm md:prose-base max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                  prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-ul:list-disc prose-ul:pl-5 prose-ul:mb-4
                  prose-ol:list-decimal prose-ol:pl-5 prose-ol:mb-4
                  prose-li:text-foreground-700 prose-li:mb-1.5 prose-li:leading-relaxed
                  prose-strong:text-foreground-900 prose-strong:font-semibold
                  prose-table:w-full prose-table:border-collapse prose-table:mb-4
                  prose-th:border prose-th:border-background-300 prose-th:px-3 prose-th:py-2 prose-th:bg-background-100 prose-th:text-sm prose-th:text-foreground-800 prose-th:font-medium prose-th:text-left
                  prose-td:border prose-td:border-background-200/70 prose-td:px-3 prose-td:py-2 prose-td:text-sm prose-td:text-foreground-700"
                dangerouslySetInnerHTML={{ __html: article.content }}
              ></div>

              <div className="mt-8 pt-6 border-t border-background-200/70">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-600 whitespace-nowrap">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-block px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="w-full bg-background-100 py-10 md:py-14">
              <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 md:mb-8">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/article/${rel.slug}`}
                      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative w-full h-40 md:h-44 overflow-hidden">
                        <img
                          src={rel.imageUrl}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug">
                          {rel.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-2 whitespace-nowrap">{rel.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}